import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export const useActivitesCommerciales = (options = {}) => {
  const { autoLoad = true, filters = {} } = options;
  const filtersRef = useRef(filters);

  const [activites, setActivites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadActivites = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filtersRef.current, ...customFilters };
      const response = await api.activitesCommerciales.getAll(params);
      setActivites(response.activites || []);
      return response.activites;
    } catch (err) {
      const message = err.message || 'Erreur lors du chargement des activités';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadParProspect = useCallback(async (prospectId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.activitesCommerciales.getByProspect(prospectId);
      setActivites(response.activites || []);
      return response.activites;
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createActivite = useCallback(async (data) => {
    setError(null);
    try {
      const response = await api.activitesCommerciales.create(data);
      const newActivite = response.activite;
      setActivites(prev => [newActivite, ...prev]);
      return { success: true, activite: newActivite };
    } catch (err) {
      const message = err.message || 'Erreur lors de la création';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const updateActivite = useCallback(async (id, data) => {
    setError(null);
    try {
      const response = await api.activitesCommerciales.update(id, data);
      const updated = response.activite;
      setActivites(prev => prev.map(a => a.id === id ? updated : a));
      return { success: true, activite: updated };
    } catch (err) {
      const message = err.message || 'Erreur lors de la mise à jour';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const deleteActivite = useCallback(async (id) => {
    setError(null);
    try {
      await api.activitesCommerciales.delete(id);
      setActivites(prev => prev.filter(a => a.id !== id));
      return { success: true };
    } catch (err) {
      const message = err.message || 'Erreur lors de la suppression';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadActivites();
    }
  }, [autoLoad, loadActivites]);

  return {
    activites,
    loading,
    error,
    loadActivites,
    loadParProspect,
    createActivite,
    updateActivite,
    deleteActivite,
  };
};

export default useActivitesCommerciales;
